import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeftIcon, ZoomInIcon, XIcon } from 'lucide-react';
import { useBackNavigation } from '../hooks/useBackNavigation';
export function Fizkultura() {
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const goBack = useBackNavigation('/work');
  return <div className="w-full min-h-screen bg-white" data-id="element-1514">
      {/* Image Modal */}
      {selectedImage && <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 p-4" onClick={() => setSelectedImage(null)} data-id="element-1515">
          <button onClick={() => setSelectedImage(null)} className="absolute top-4 right-4 text-white hover:text-emerald-400 transition-colors" data-id="element-1516">
            <XIcon className="w-8 h-8" data-id="element-1517" />
          </button>
          <motion.img initial={{
        scale: 0.9
      }} animate={{
        scale: 1
      }} src={selectedImage} alt="Enlarged view" className="max-w-full max-h-full object-contain" data-id="element-1518" />
        </motion.div>}
      {/* Header */}
      <header className="py-4 px-6 bg-white border-b border-stone-200 sticky top-0 z-40" data-id="element-1519">
        <div className="max-w-7xl mx-auto flex items-center justify-between" data-id="element-1520">
          <button onClick={goBack} className="inline-flex items-center gap-2 text-stone-600 hover:text-stone-900 transition-colors" data-id="element-1521">
            <ArrowLeftIcon className="w-5 h-5" data-id="element-1522" />
            <span className="font-light" data-id="element-1523">Back</span>
          </button>
          <h1 className="text-2xl font-light text-stone-900" data-id="element-1524">Maria Elina</h1>
        </div>
      </header>
      {/* Hero Section */}
      <section className="py-20 px-6 bg-gradient-to-br from-stone-50 via-emerald-50/30 to-stone-50" data-id="element-1525">
        <div className="max-w-5xl mx-auto" data-id="element-1526">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8
        }} data-id="element-1527">
            <p className="text-emerald-600 font-light tracking-wide uppercase mb-4" data-id="element-1528">
              Creative Direction, Brand Strategy
            </p>
            <h1 className="text-4xl md:text-6xl font-light text-stone-900 mb-6" data-id="element-1529">
              Fizkultura
            </h1>
            <p className="text-xl text-stone-600 font-light mb-8" data-id="element-1530">
              Studio planning and design, creative direction and brand strategy
              development for a fitness space focused on community building
            </p>
            <div className="space-y-4 text-stone-700 font-light" data-id="element-1531">
              <p className="text-lg" data-id="element-1532">
                <span className="font-normal text-stone-900" data-id="element-1533">Role:</span>{' '}
                Creative Director, Project Manager, Marketing Lead
              </p>
              <p className="text-lg" data-id="element-1534">
                <span className="font-normal text-stone-900" data-id="element-1535">Scope:</span> Brand
                Identity, Spatial Planning, Visual System
              </p>
              <p className="text-lg" data-id="element-1536">
                <span className="font-normal text-stone-900" data-id="element-1537">Status:</span>{' '}
                Completed
              </p>
              <p className="text-lg" data-id="element-1538">
                <span className="font-normal text-stone-900" data-id="element-1539">Year:</span> 2024
              </p>
            </div>
          </motion.div>
        </div>
      </section>
      {/* Project Overview */}
      <section className="py-20 px-6 bg-white" data-id="element-1540">
        <div className="max-w-5xl mx-auto" data-id="element-1541">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8
        }} data-id="element-1542">
            <h2 className="text-3xl font-light text-stone-900 mb-6" data-id="element-1543">
              Project Overview
            </h2>
            <p className="text-lg text-stone-600 font-light leading-relaxed mb-6" data-id="element-1544">
              Fizkultura represents a holistic approach to fitness space design,
              where community and wellness intersect. The project encompasses
              comprehensive studio planning, spatial design, and brand strategy
              development.
            </p>
            <p className="text-lg text-stone-600 font-light leading-relaxed mb-6" data-id="element-1545">
              Through thoughtful creative direction, the space fosters
              connection and motivation, creating an environment where members
              feel supported in their fitness journey while building lasting
              relationships.
            </p>
            <p className="text-lg text-stone-600 font-light leading-relaxed" data-id="element-1546">
              The brand identity system reflects the studio's values of
              strength, community, and accessibility—combining bold Cyrillic
              typography with a distinctive teal color palette that evokes both
              energy and calm.
            </p>
          </motion.div>
        </div>
      </section>
      {/* Brand Identity Hero */}
      <section className="py-20 px-6 bg-stone-50" data-id="element-1547">
        <div className="max-w-7xl mx-auto" data-id="element-1548">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8
        }} data-id="element-1549">
            <h2 className="text-3xl font-light text-stone-900 mb-4" data-id="element-1550">
              Brand Identity System
            </h2>
            <p className="text-lg text-stone-600 font-light mb-12 max-w-3xl" data-id="element-1551">
              A comprehensive visual identity that balances strength with
              approachability, creating a distinctive presence in the fitness
              space.
            </p>
            <motion.div initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6
          }} className="group cursor-pointer" onClick={() => setSelectedImage('https://storage.yandexcloud.net/maria-images/images/fizkultura_ready/image%2067.png')} data-id="element-1552">
              <div className="relative overflow-hidden rounded-2xl shadow-xl" data-id="element-1553">
                <img src="https://storage.yandexcloud.net/maria-images/images/fizkultura_ready/image%2067.png" alt="Fizkultura Brand Identity System" className="w-full h-auto" data-id="element-1554" />
                <div className="absolute inset-0 bg-stone-900/0 group-hover:bg-stone-900/10 transition-colors duration-300 flex items-center justify-center" data-id="element-1555">
                  <ZoomInIcon className="w-12 h-12 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" data-id="element-1556" />
                </div>
              </div>
              <p className="text-stone-600 font-light mt-4 text-center italic" data-id="element-1557">
                Complete brand identity system with logo variations and
                applications
              </p>
            </motion.div>
          </motion.div>
        </div>
      </section>
      {/* Brand Applications */}
      <section className="py-20 px-6 bg-white" data-id="element-1558">
        <div className="max-w-7xl mx-auto" data-id="element-1559">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8
        }} data-id="element-1560">
            <h2 className="text-3xl font-light text-stone-900 mb-4" data-id="element-1561">
              Brand Applications
            </h2>
            <p className="text-lg text-stone-600 font-light mb-12 max-w-3xl" data-id="element-1562">
              The identity system comes to life across multiple touchpoints—from
              physical spaces to merchandise and digital platforms.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-1563">
              {/* Left Column */}
              <div className="space-y-8" data-id="element-1564">
                <motion.div initial={{
                opacity: 0,
                y: 30
              }} whileInView={{
                opacity: 1,
                y: 0
              }} viewport={{
                once: true
              }} transition={{
                duration: 0.6
              }} className="group cursor-pointer" onClick={() => setSelectedImage('https://storage.yandexcloud.net/maria-images/images/fizkultura_ready/image%2068.png')} data-id="element-1565">
                  <div className="relative overflow-hidden rounded-xl shadow-lg" data-id="element-1566">
                    <img src="https://storage.yandexcloud.net/maria-images/images/fizkultura_ready/image%2068.png" alt="Brand applications - merchandise and signage" className="w-full h-auto" data-id="element-1567" />
                    <div className="absolute inset-0 bg-stone-900/0 group-hover:bg-stone-900/10 transition-colors duration-300 flex items-center justify-center" data-id="element-1568">
                      <ZoomInIcon className="w-8 h-8 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" data-id="element-1569" />
                    </div>
                  </div>
                  <div className="mt-4" data-id="element-1570">
                    <h4 className="text-lg font-light text-stone-900 mb-2" data-id="element-1571">
                      Selected Design in Application
                    </h4>
                    <p className="text-stone-600 font-light text-sm" data-id="element-1572">
                      Merchandise, signage, and environmental graphics
                    </p>
                  </div>
                </motion.div>
                <motion.div initial={{
                opacity: 0,
                y: 30
              }} whileInView={{
                opacity: 1,
                y: 0
              }} viewport={{
                once: true
              }} transition={{
                duration: 0.6,
                delay: 0.1
              }} className="group cursor-pointer" onClick={() => setSelectedImage('https://storage.yandexcloud.net/maria-images/images/fizkultura_ready/%D0%BA1.jpg')} data-id="element-1573">
                  <div className="relative overflow-hidden rounded-xl shadow-lg" data-id="element-1574">
                    <img src="https://storage.yandexcloud.net/maria-images/images/fizkultura_ready/%D0%BA1.jpg" alt="Social media promotional content" className="w-full h-auto" data-id="element-1575" />
                    <div className="absolute inset-0 bg-stone-900/0 group-hover:bg-stone-900/10 transition-colors duration-300 flex items-center justify-center" data-id="element-1576">
                      <ZoomInIcon className="w-8 h-8 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" data-id="element-1577" />
                    </div>
                  </div>
                  <div className="mt-4" data-id="element-1578">
                    <h4 className="text-lg font-light text-stone-900 mb-2" data-id="element-1579">
                      Social Media Promotion
                    </h4>
                    <p className="text-stone-600 font-light text-sm" data-id="element-1580">
                      Dynamic promotional content for digital campaigns
                    </p>
                  </div>
                </motion.div>
              </div>
              {/* Right Column */}
              <motion.div initial={{
              opacity: 0,
              y: 30
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.6,
              delay: 0.2
            }} className="group cursor-pointer" onClick={() => setSelectedImage('https://storage.yandexcloud.net/maria-images/images/fizkultura_ready/iPhone%2014%20-%201.png')} data-id="element-1581">
                <div className="relative overflow-hidden rounded-xl shadow-lg bg-stone-900" data-id="element-1582">
                  <img src="https://storage.yandexcloud.net/maria-images/images/fizkultura_ready/iPhone%2014%20-%201.png" alt="Mobile application mockup" className="w-full h-auto" data-id="element-1583" />
                  <div className="absolute inset-0 bg-stone-900/0 group-hover:bg-stone-900/10 transition-colors duration-300 flex items-center justify-center" data-id="element-1584">
                    <ZoomInIcon className="w-8 h-8 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" data-id="element-1585" />
                  </div>
                </div>
                <div className="mt-4" data-id="element-1586">
                  <h4 className="text-lg font-light text-stone-900 mb-2" data-id="element-1587">
                    Digital Experience
                  </h4>
                  <p className="text-stone-600 font-light text-sm" data-id="element-1588">
                    For future mobile app interface with community features
                  </p>
                </div>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>
      {/* Developed Website */}
      <section className="py-20 px-6 bg-white" data-id="element-1589">
        <div className="max-w-7xl mx-auto" data-id="element-1590">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8
        }} data-id="element-1591">
            <h2 className="text-3xl font-light text-stone-900 mb-4" data-id="element-1592">
              Website Development
            </h2>
            <p className="text-lg text-stone-600 font-light mb-12 max-w-3xl" data-id="element-1593">
              Working closely with developers, we brought the brand identity to
              life through a fully functional website. The site features class
              booking, membership management, promotional campaigns, and
              location information—all designed to create a seamless user
              experience that reflects the studio's welcoming atmosphere.
            </p>
            <motion.div initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6
          }} className="group cursor-pointer" onClick={() => setSelectedImage('https://storage.yandexcloud.net/maria-images/images/fizkultura_ready/Group%206.png')} data-id="element-1594">
              <div className="relative overflow-hidden rounded-2xl shadow-xl" data-id="element-1595">
                <img src="https://storage.yandexcloud.net/maria-images/images/fizkultura_ready/Group%206.png" alt="Fizkultura developed website" className="w-full h-auto" data-id="element-1596" />
                <div className="absolute inset-0 bg-stone-900/0 group-hover:bg-stone-900/10 transition-colors duration-300 flex items-center justify-center" data-id="element-1597">
                  <ZoomInIcon className="w-12 h-12 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" data-id="element-1598" />
                </div>
              </div>
              <p className="text-stone-600 font-light mt-4 text-center italic" data-id="element-1599">
                Complete website showcasing class schedules, membership options,
                and community features
              </p>
            </motion.div>
          </motion.div>
        </div>
      </section>
      {/* Completed Studio Space */}
      <section className="py-20 px-6 bg-stone-50" data-id="element-1600">
        <div className="max-w-7xl mx-auto" data-id="element-1601">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8
        }} data-id="element-1602">
            <h2 className="text-3xl font-light text-stone-900 mb-4" data-id="element-1603">
              Completed Studio Space
            </h2>
            <p className="text-lg text-stone-600 font-light mb-12 max-w-3xl" data-id="element-1604">
              The transformation from empty office space to vibrant fitness
              studio involved careful selection of furnishings, custom signage
              design, and creative repurposing of industrial lockers.
            </p>
            {/* Studio Space Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-1605">
              {/* Custom Signage */}
              <motion.div initial={{
              opacity: 0,
              y: 30
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.6
            }} className="group cursor-pointer" onClick={() => setSelectedImage('https://storage.yandexcloud.net/maria-images/images/fizkultura_ready/photo_2023-06-28_12-.jpg')} data-id="element-1606">
                <div className="relative overflow-hidden rounded-lg shadow-lg" data-id="element-1607">
                  <img src="https://storage.yandexcloud.net/maria-images/images/fizkultura_ready/photo_2023-06-28_12-.jpg" alt="Custom signage and wayfinding" className="w-full h-64 object-cover" data-id="element-1608" />
                  <div className="absolute inset-0 bg-stone-900/0 group-hover:bg-stone-900/10 transition-colors duration-300 flex items-center justify-center" data-id="element-1609">
                    <ZoomInIcon className="w-8 h-8 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" data-id="element-1610" />
                  </div>
                </div>
                <div className="mt-3" data-id="element-1611">
                  <h4 className="text-base font-light text-stone-900 mb-1" data-id="element-1612">
                    Custom Signage
                  </h4>
                  <p className="text-stone-600 font-light text-sm" data-id="element-1613">
                    Motivational graphics and wayfinding
                  </p>
                </div>
              </motion.div>
              {/* Community Lounge */}
              <motion.div initial={{
              opacity: 0,
              y: 30
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.6,
              delay: 0.1
            }} className="group cursor-pointer" onClick={() => setSelectedImage('https://storage.yandexcloud.net/maria-images/images/fizkultura_ready/DSC_0116%20(1).jpg')} data-id="element-1614">
                <div className="relative overflow-hidden rounded-lg shadow-lg" data-id="element-1615">
                  <img src="https://storage.yandexcloud.net/maria-images/images/fizkultura_ready/DSC_0116%20(1).jpg" alt="Community lounge space" className="w-full h-64 object-cover" data-id="element-1616" />
                  <div className="absolute inset-0 bg-stone-900/0 group-hover:bg-stone-900/10 transition-colors duration-300 flex items-center justify-center" data-id="element-1617">
                    <ZoomInIcon className="w-8 h-8 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" data-id="element-1618" />
                  </div>
                </div>
                <div className="mt-3" data-id="element-1619">
                  <h4 className="text-base font-light text-stone-900 mb-1" data-id="element-1620">
                    Community Lounge
                  </h4>
                  <p className="text-stone-600 font-light text-sm" data-id="element-1621">
                    Social space with games and seating
                  </p>
                </div>
              </motion.div>
              {/* Changing Facilities */}
              <motion.div initial={{
              opacity: 0,
              y: 30
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.6,
              delay: 0.2
            }} className="group cursor-pointer" onClick={() => setSelectedImage('https://storage.yandexcloud.net/maria-images/images/fizkultura_ready/DSC_0080.jpg')} data-id="element-1622">
                <div className="relative overflow-hidden rounded-lg shadow-lg" data-id="element-1623">
                  <img src="https://storage.yandexcloud.net/maria-images/images/fizkultura_ready/DSC_0080.jpg" alt="Changing facilities" className="w-full h-64 object-cover" data-id="element-1624" />
                  <div className="absolute inset-0 bg-stone-900/0 group-hover:bg-stone-900/10 transition-colors duration-300 flex items-center justify-center" data-id="element-1625">
                    <ZoomInIcon className="w-8 h-8 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" data-id="element-1626" />
                  </div>
                </div>
                <div className="mt-3" data-id="element-1627">
                  <h4 className="text-base font-light text-stone-900 mb-1" data-id="element-1628">
                    Changing Facilities
                  </h4>
                  <p className="text-stone-600 font-light text-sm" data-id="element-1629">
                    Clean, functional design
                  </p>
                </div>
              </motion.div>
              {/* Custom Locker Art */}
              <motion.div initial={{
              opacity: 0,
              y: 30
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.6,
              delay: 0.3
            }} className="group cursor-pointer" onClick={() => setSelectedImage('https://storage.yandexcloud.net/maria-images/images/fizkultura_ready/DSC_0091.jpg')} data-id="element-1630">
                <div className="relative overflow-hidden rounded-lg shadow-lg" data-id="element-1631">
                  <img src="https://storage.yandexcloud.net/maria-images/images/fizkultura_ready/DSC_0091.jpg" alt="Repurposed lockers with botanical artwork" className="w-full h-64 object-cover" data-id="element-1632" />
                  <div className="absolute inset-0 bg-stone-900/0 group-hover:bg-stone-900/10 transition-colors duration-300 flex items-center justify-center" data-id="element-1633">
                    <ZoomInIcon className="w-8 h-8 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" data-id="element-1634" />
                  </div>
                </div>
                <div className="mt-3" data-id="element-1635">
                  <h4 className="text-base font-light text-stone-900 mb-1" data-id="element-1636">
                    Custom Locker Art
                  </h4>
                  <p className="text-stone-600 font-light text-sm" data-id="element-1637">
                    Botanical illustrations on repurposed lockers
                  </p>
                </div>
              </motion.div>
              {/* Training Space */}
              <motion.div initial={{
              opacity: 0,
              y: 30
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.6,
              delay: 0.4
            }} className="group cursor-pointer" onClick={() => setSelectedImage('https://storage.yandexcloud.net/maria-images/images/fizkultura_ready/photo_2024-10-19_22-58-28%20(3).jpg')} data-id="element-1638">
                <div className="relative overflow-hidden rounded-lg shadow-lg" data-id="element-1639">
                  <img src="https://storage.yandexcloud.net/maria-images/images/fizkultura_ready/photo_2024-10-19_22-58-28%20(3).jpg" alt="Training space with red flooring" className="w-full h-64 object-cover" data-id="element-1640" />
                  <div className="absolute inset-0 bg-stone-900/0 group-hover:bg-stone-900/10 transition-colors duration-300 flex items-center justify-center" data-id="element-1641">
                    <ZoomInIcon className="w-8 h-8 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" data-id="element-1642" />
                  </div>
                </div>
                <div className="mt-3" data-id="element-1643">
                  <h4 className="text-base font-light text-stone-900 mb-1" data-id="element-1644">
                    Training Area
                  </h4>
                  <p className="text-stone-600 font-light text-sm" data-id="element-1645">
                    Red flooring with functional equipment
                  </p>
                </div>
              </motion.div>
              {/* Yoga Class in Session */}
              <motion.div initial={{
              opacity: 0,
              y: 30
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.6,
              delay: 0.5
            }} className="group cursor-pointer" onClick={() => setSelectedImage('https://storage.yandexcloud.net/maria-images/images/fizkultura_ready/photo_2024-10-19_22-58-29.jpg')} data-id="element-1646">
                <div className="relative overflow-hidden rounded-lg shadow-lg" data-id="element-1647">
                  <img src="https://storage.yandexcloud.net/maria-images/images/fizkultura_ready/photo_2024-10-19_22-58-29.jpg" alt="Yoga class in session" className="w-full h-64 object-cover" data-id="element-1648" />
                  <div className="absolute inset-0 bg-stone-900/0 group-hover:bg-stone-900/10 transition-colors duration-300 flex items-center justify-center" data-id="element-1649">
                    <ZoomInIcon className="w-8 h-8 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" data-id="element-1650" />
                  </div>
                </div>
                <div className="mt-3" data-id="element-1651">
                  <h4 className="text-base font-light text-stone-900 mb-1" data-id="element-1652">
                    Classes in Action
                  </h4>
                  <p className="text-stone-600 font-light text-sm" data-id="element-1653">
                    Yoga and stretching sessions
                  </p>
                </div>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>
      {/* Design Approach */}
      <section className="py-20 px-6 bg-stone-50" data-id="element-1654">
        <div className="max-w-5xl mx-auto" data-id="element-1655">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8
        }} data-id="element-1656">
            <h2 className="text-3xl font-light text-stone-900 mb-8" data-id="element-1657">
              Design Approach
            </h2>
            <div className="space-y-6 text-lg text-stone-600 font-light leading-relaxed" data-id="element-1658">
              <p data-id="element-1659">
                The design strategy centers on creating a welcoming environment
                that breaks down barriers to fitness. The bold Cyrillic
                typography establishes a strong, confident identity while the
                teal color palette adds warmth and accessibility.
              </p>
              <p data-id="element-1660">
                Spatial planning prioritizes flexible zones that accommodate
                diverse fitness activities—from high-intensity training to
                mindful movement practices. The layout encourages natural
                interaction between members while maintaining comfortable
                personal space.
              </p>
              <p data-id="element-1661">
                Every touchpoint, from signage to digital interfaces, reinforces
                the brand's commitment to community and inclusivity. The result
                is a fitness space that feels both energizing and supportive—a
                place where people don't just work out, they belong.
              </p>
            </div>
          </motion.div>
        </div>
      </section>
      {/* Branding Ideation - Moved to bottom, smaller scale */}
      <section className="py-16 px-6 bg-white border-t border-stone-200" data-id="element-1662">
        <div className="max-w-6xl mx-auto" data-id="element-1663">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8
        }} data-id="element-1664">
            <h2 className="text-2xl font-light text-stone-900 mb-3" data-id="element-1665">
              Branding Ideation Process
            </h2>
            <p className="text-base text-stone-600 font-light mb-8 max-w-3xl" data-id="element-1666">
              The brand identity emerged through exploration of multiple visual
              directions—from geometric symbols to typographic solutions to
              Cyrillic monograms.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-1667">
              {/* Triangle Concept */}
              <motion.div initial={{
              opacity: 0,
              y: 30
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.6
            }} className="group cursor-pointer" onClick={() => setSelectedImage('https://uploadthingy.s3.us-west-1.amazonaws.com/gJk81STFr6Kd4kj96qgKkF/image_63.png')} data-id="element-1668">
                <div className="relative overflow-hidden rounded-lg shadow-lg" data-id="element-1669">
                  <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/gJk81STFr6Kd4kj96qgKkF/image_63.png" alt="Triangle logo concept" className="w-full h-48 object-cover" data-id="element-1670" />
                  <div className="absolute inset-0 bg-stone-900/0 group-hover:bg-stone-900/10 transition-colors duration-300 flex items-center justify-center" data-id="element-1671">
                    <ZoomInIcon className="w-8 h-8 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" data-id="element-1672" />
                  </div>
                </div>
                <div className="mt-3" data-id="element-1673">
                  <h4 className="text-sm font-light text-stone-900 mb-1" data-id="element-1674">
                    Geometric Symbol
                  </h4>
                  <p className="text-stone-600 font-light text-xs" data-id="element-1675">
                    Triangle-based exploration
                  </p>
                </div>
              </motion.div>
              {/* Typographic Concept */}
              <motion.div initial={{
              opacity: 0,
              y: 30
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.6,
              delay: 0.1
            }} className="group cursor-pointer" onClick={() => setSelectedImage('https://uploadthingy.s3.us-west-1.amazonaws.com/kN7rAcV3HzB5nkb4wv7zAt/image_62.png')} data-id="element-1676">
                <div className="relative overflow-hidden rounded-lg shadow-lg" data-id="element-1677">
                  <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/kN7rAcV3HzB5nkb4wv7zAt/image_62.png" alt="Typographic concept" className="w-full h-48 object-cover" data-id="element-1678" />
                  <div className="absolute inset-0 bg-stone-900/0 group-hover:bg-stone-900/10 transition-colors duration-300 flex items-center justify-center" data-id="element-1679">
                    <ZoomInIcon className="w-8 h-8 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" data-id="element-1680" />
                  </div>
                </div>
                <div className="mt-3" data-id="element-1681">
                  <h4 className="text-sm font-light text-stone-900 mb-1" data-id="element-1682">
                    Typographic Solution
                  </h4>
                  <p className="text-stone-600 font-light text-xs" data-id="element-1683">
                    Bold stacked letterforms
                  </p>
                </div>
              </motion.div>
              {/* Cyrillic Monogram */}
              <motion.div initial={{
              opacity: 0,
              y: 30
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.6,
              delay: 0.2
            }} className="group cursor-pointer" onClick={() => setSelectedImage('https://uploadthingy.s3.us-west-1.amazonaws.com/aqT3zkMNpFR7PFpSjvuhuA/image_61.png')} data-id="element-1684">
                <div className="relative overflow-hidden rounded-lg shadow-lg" data-id="element-1685">
                  <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/aqT3zkMNpFR7PFpSjvuhuA/image_61.png" alt="Cyrillic monogram" className="w-full h-48 object-cover" data-id="element-1686" />
                  <div className="absolute inset-0 bg-stone-900/0 group-hover:bg-stone-900/10 transition-colors duration-300 flex items-center justify-center" data-id="element-1687">
                    <ZoomInIcon className="w-8 h-8 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" data-id="element-1688" />
                  </div>
                </div>
                <div className="mt-3" data-id="element-1689">
                  <h4 className="text-sm font-light text-stone-900 mb-1" data-id="element-1690">
                    Cyrillic Monogram
                  </h4>
                  <p className="text-stone-600 font-light text-xs" data-id="element-1691">
                    ФК monogram with wordmark
                  </p>
                </div>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>;
}